import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, styled } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Login from './pages/Login';
import Profile from './pages/Profile';
import Users from './pages/Users';
import Visitors from './pages/Visitors';
import Departments from './pages/Departments';
import VisitsRoute from './pages/VisitsRoute';
import Absences from './pages/Absences';
import AbsenceTypes from './pages/AbsenceTypes';
import AbsenceStats from './pages/AbsenceStats';
import AbsenceDashboard from './pages/AbsenceDashboard';
import NotFound from './pages/NotFound';
import Functions from './pages/Functions';
import Dashboard from './pages/Dashboard';
import AppointmentForm from './pages/AppointmentForm';
import AppointmentsRoute from './pages/AppointmentsRoute';

const theme = createTheme({
  palette: {
    primary: {
      main: '#d21433',
      dark: '#a50e26',
      light: '#e84060',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#006d78',
      dark: '#005a63',
      light: '#00919f',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ffc71f',
      contrastText: '#3d2000',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          background: 'linear-gradient(135deg, #d21433 0%, #a50e26 100%)',
          '&:hover': { background: 'linear-gradient(135deg, #a50e26 0%, #8b0c1e 100%)' },
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #006d78 0%, #005a63 100%)',
          '&:hover': { background: 'linear-gradient(135deg, #005a63 0%, #004a52 100%)' },
        },
      },
    },
  },
});

// Composant pour le contenu principal avec marge pour la barre d'application
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    flexGrow: 1,
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1),
    paddingLeft: 0,
    paddingRight: 0,
    marginTop: theme.spacing(0),
    background: 'linear-gradient(160deg, #e6f4f6 0%, #f0fafb 40%, #ffffff 100%)',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  }),
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Navbar />
          <Main sx={{ flex: 1 }}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/appointment" element={<AppointmentForm />} />
              <Route path="/appointments" element={<AppointmentsRoute />} />
              <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
              <Route path="/users" element={<PrivateRoute adminOnly><Users /></PrivateRoute>} />
              <Route path="/visitors" element={<PrivateRoute adminOnly><Visitors /></PrivateRoute>} />
              <Route path="/departments" element={<PrivateRoute><Departments /></PrivateRoute>} />
              <Route path="/functions" element={<PrivateRoute><Functions /></PrivateRoute>} />
              <Route path="/visits" element={<VisitsRoute />} />
              <Route path="/absences" element={<PrivateRoute><Absences /></PrivateRoute>} />
              <Route path="/absence-types" element={<PrivateRoute><AbsenceTypes /></PrivateRoute>} />
              <Route path="/absence-stats" element={<PrivateRoute><AbsenceStats /></PrivateRoute>} />
              <Route path="/absence-dashboard" element={<PrivateRoute><AbsenceDashboard /></PrivateRoute>} />
              <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Main>
          <Footer />
        </Box>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 
